import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {setEntities} from '@ngneat/elf-entities';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {ManageUser, User, UserPreferencesEnum, UsersFilters, UsersPagination} from '@store/settings/users/users.model';
import {usersStore} from '@store/settings/users/users.store';
import {getUsersDataSource} from '@store/settings/users/users.requests';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter} from '@store/common/tableFilter.model';
import fileDownload from 'js-file-download';
import {extractFilenameFromResponse} from '@utils/response.utils';

export class UsersService {
  store = usersStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: UsersFilters.initTableFilters()},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getUsers = (): Observable<UsersPagination> => {
    const {filters: {search, tableFilters}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETUsers(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<UsersPagination>) => {
          return response.data;
        }),
        tap((userPagination) => {
          const paginationData = Pagination.toPaginationData(userPagination, tablePagination.page);

          this.store.update(
            setEntities(userPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, userPagination.data.map((user) => user.id)),
            getUsersDataSource.setSuccess(),
          );
        }),
        getUsersDataSource.trackRequestStatus(),
      );
  };

  refreshUsers = () => {
    return from(
      APIAxios({
        ...APIRoutes.GETRefreshUsers()
      })
    )
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data.message, 'error');
        }),
        tap(() => this.getUsers()),
        map((response: AxiosResponse<{ insertedCount: number; deletedCount: number }>) => {
          return response.data;
        })
      );
  };

  importUsersFromCsv = (file: File) => {
    const formData = new FormData();

    formData.append('document', file);

    return from(
      APIAxios({
        ...APIRoutes.POSTCsvUsers(),
        data: formData
      }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data.message, 'error');
        }),
        tap(() => this.getUsers()),
        map((response: AxiosResponse<{ insertedCount: number; updatedCount: number }>) => {
          return response.data;
        })
      );
  };

  saveUser = (data: ManageUser, userId: number | string): Observable<ManageUser> => {
    return from(APIAxios({
      ...APIRoutes.PATCHUpdateUser(userId),
      data: {
        ...data,
        userPreferences: {
          emailOnExecutionToBeTreated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionToBeTreated),
          emailOnExecutionAccepted: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionAccepted),
          emailOnExecutionDenied: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionDenied),
          emailOnExecutionEnded: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionEnded),
          emailOnExecutionNewComment: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionNewComment),
          emailOnExecutionNewIssue: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionNewIssue),
          emailOnExecutionIssueClosed: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionIssueClosed),
          emailOnExecutionUpdated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionUpdated),
          emailOnExecutionIssueUpdated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionIssueUpdated),
          emailOnExecutionCanceled: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionCanceled),
        }
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<User>) => {
          return User.toManageUser(response.data);
        }),
        switchMap((user) => this.getUsers().pipe(map(() => user))),
      );

  };

  createUser(data: ManageUser) {
    return from(APIAxios({
      ...APIRoutes.POSTUser(),
      data: {
        ...data,
        userPreferences: {
          emailOnExecutionToBeTreated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionToBeTreated),
          emailOnExecutionAccepted: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionAccepted),
          emailOnExecutionDenied: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionDenied),
          emailOnExecutionEnded: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionEnded),
          emailOnExecutionNewComment: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionNewComment),
          emailOnExecutionNewIssue: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionNewIssue),
          emailOnExecutionIssueClosed: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionIssueClosed),
          emailOnExecutionUpdated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionUpdated),
          emailOnExecutionIssueUpdated: data.userPreferences?.includes(UserPreferencesEnum.emailOnExecutionIssueUpdated)
        }
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error', true);
          }
        ),
        map((response: AxiosResponse<User>) => {
          return User.toManageUser(response.data);
        }),
        switchMap((user) => this.getUsers().pipe(map(() => user)))
      );
  }

  getUserById = (userId: number | string): Observable<ManageUser> => {
    return from(APIAxios(APIRoutes.GETUserById(userId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<User>) => {
          return User.toManageUser(response.data);
        }),
      );
  };

  getUsersCSV() {
    return from(APIAxios({
      ...APIRoutes.GETUsersCSV(),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response) => {
          const filename = extractFilenameFromResponse(response);
          console.log(filename);
          fileDownload(response.data, filename || 'users.xlsx');
        }),
      );
  }
}

export const usersService = new UsersService();
